// src/pages/SystemConfiguration/index.tsx
// Main page component for System Configuration with sidebar layout

import React, { useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import BreadCrumb from 'Components/Common/BreadCrumb';
import ConfigSidebar from './components/ConfigSidebar';
import ConfigContentCard from './components/ConfigContentCard';
import RegionConfigForm from './components/RegionConfigForm';
import LookupConfigForm from './components/LookupConfigForm';
// Mục 8bis (README §8) — 3 tab Hoa hồng / Giá vé / Ngưỡng thuế TNCN đã gộp vào RegionConfigForm.
// Giữ lại 3 component + thunk/command riêng để tương thích ngược, chỉ bỏ khỏi UI.

import { setActiveTab } from 'slices/systemConfiguration/reducer';
import { updateCrossPrizePayoutConfig, updatePrizeClaimValidityConfig } from 'slices/systemConfiguration/thunk';
import { fetchActiveTicketTypes } from 'slices/ticketType/thunk';
import { getAuthUser } from 'helpers/tokenHelper';
import { getTenantById } from 'helpers/fakebackend_helper';
import { loadSystemConfigWithCache } from 'helpers/systemConfigCache';
import { useBrandingTitle } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('system_configuration');

const SystemConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const regionFormRef = useRef<any>(null);
  const lookupFormRef = useRef<any>(null);

  const { config, loading, activeTab, error } = useSelector(
    (state: any) => state.SystemConfiguration
  );
  const { activeTicketTypes, activeLoading: ticketTypesLoading } = useSelector(
    (state: any) => state.TicketType
  );
  
  // State cho tenant info
  const [tenantInfo, setTenantInfo] = React.useState<{
    name: string;
    logo?: string;
  }>({ name: st('cong_ty_tnhh_mtv_xo_so_text') });
  
    useEffect(() => {
    const loadTenantAndConfig = async () => {
      try {
        // Decode JWT token để lấy TenantId
        const authUser = getAuthUser();
        const token = authUser?.token || authUser?.access_token;

        if (token) {
          // Decode JWT payload
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const payload = JSON.parse(jsonPayload);
          const tenantId = parseInt(payload.TenantId || '1', 10);

          // Fetch tenant info
          const tenantResponse = await getTenantById(tenantId);
          const tenant = tenantResponse.data || tenantResponse;

          setTenantInfo({
            name: tenant.name || st('cong_ty_tnhh_mtv_xo_so_text'),
            logo: tenant.logoUrl,
          });
        }
      } catch (error) {
        console.error('Error loading tenant info:', error);
        // Sử dụng giá trị mặc định nếu có lỗi
        setTenantInfo({
          name: st('cong_ty_tnhh_mtv_xo_so_text'),
        });
      }

      await loadSystemConfigWithCache(dispatch);
    };

    loadTenantAndConfig();
    dispatch(fetchActiveTicketTypes());
  }, [dispatch]);
  
    const handleTabChange = (tab: 'region' | 'lookup') => {
    if (activeTab !== tab) {
      dispatch(setActiveTab(tab));
    }
  };

  const handleReset = () => {
    if (activeTab === 'region' && regionFormRef.current) {
      regionFormRef.current.resetForm();
    } else if (activeTab === 'lookup' && lookupFormRef.current) {
      lookupFormRef.current.resetForm();
    }
  };

  const handleSave = () => {
    if (activeTab === 'region' && regionFormRef.current) {
      regionFormRef.current.submitForm();
    } else if (activeTab === 'lookup' && lookupFormRef.current) {
      lookupFormRef.current.submitForm();
    }
  };

  // Mục 8.2 (README_UPDATE.md) — gạt công tắc là lưu ngay (hiệu lực tức thì), không đợi nút Lưu chung
  // của form Profile Vùng miền (form đó hiệu lực "kỳ tiếp theo").
  const handleToggleCrossPrizePayout = (checked: boolean) => {
    dispatch(updateCrossPrizePayoutConfig({ allowAgentCrossPrizePayout: checked }));
  };

  // Thời hạn trả thưởng hợp lệ — nhập số ngày rồi bấm Lưu (hiệu lực ngay, giống công tắc trả thưởng chéo).
  const [validDaysInput, setValidDaysInput] = React.useState<string>('');
  useEffect(() => {
    setValidDaysInput(String(config?.prizeClaimValidDays || 30));
  }, [config?.prizeClaimValidDays]);
  const validDaysNumber = Number(validDaysInput);
  const isValidDaysInputValid = Number.isInteger(validDaysNumber) && validDaysNumber >= 1 && validDaysNumber <= 3650;
  const handleSavePrizeClaimValidity = () => {
    if (!isValidDaysInputValid) return;
    dispatch(updatePrizeClaimValidityConfig({ prizeClaimValidDays: validDaysNumber }));
  };

  // Get current tab title and icon
  const getTabInfo = () => {
    if (activeTab === 'region') {
      return { title: st('profile_vung_mien_button'), icon: 'ri-map-pin-line' };
    }
    return { title: st('duong_dan_button'), icon: 'ri-search-line' };
  };
  
    useBrandingTitle(st('cau_hinh_he_thong_text_2'));
  
    const tabInfo = getTabInfo();
  
  if (!config) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{st('dang_tai_text')}</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('cau_hinh_he_thong_text_2')} pageTitle={st('cai_dat_text')} />

          {/* Mục 8.2 (README_UPDATE.md) — cấu hình "đại lý trả thưởng chéo", toàn hệ thống, hiệu lực ngay */}
          <Card>
            <CardBody>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h6 className="mb-1">
                    <i className="ri-exchange-dollar-line me-2 text-primary"></i>
                    {st('dai_ly_tra_thuong_cheo_title')}
                  </h6>
                  <p className="text-muted mb-0" style={{ maxWidth: 720 }}>
                    {st('khi_bat_tai_khoan_dai_ly_text')} <strong>{st('moi_ve_trung_thuong_cua_ky_text')}</strong>{st('khong_bat_buoc_ve_phai_nam_text')}
                  </p>
                </div>
                <div className="form-check form-switch form-switch-lg">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="allowAgentCrossPrizePayout"
                    checked={!!config.allowAgentCrossPrizePayout}
                    disabled={loading}
                    onChange={(e) => handleToggleCrossPrizePayout(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowAgentCrossPrizePayout">
                    {config.allowAgentCrossPrizePayout ? st('dang_bat_text') : st('dang_tat_text')}
                  </label>
                </div>
              </div>
              <hr className="my-3" />
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h6 className="mb-1">
                    <i className="ri-calendar-check-line me-2 text-primary"></i>
                    {st('thoi_han_tra_thuong_hop_le_title')}
                  </h6>
                  <p className="text-muted mb-0" style={{ maxWidth: 720 }}>
                    {st('thoi_han_tra_thuong_hop_le_desc', { days: config.prizeClaimValidDays || 30 })}
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{ width: 170 }}>
                    <input
                      type="number"
                      min={1}
                      max={3650}
                      className={`form-control${isValidDaysInputValid ? '' : ' is-invalid'}`}
                      id="prizeClaimValidDays"
                      value={validDaysInput}
                      disabled={loading}
                      onChange={(e) => setValidDaysInput(e.target.value)}
                    />
                    <span className="input-group-text">{st('ngay_text')}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading || !isValidDaysInputValid || validDaysNumber === (config.prizeClaimValidDays || 30)}
                    onClick={handleSavePrizeClaimValidity}
                  >
                    <i className="ri-save-line me-1"></i>
                    {st('luu_button')}
                  </button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Grid Layout: 300px sidebar + flexible content */}
          <Row>
            <Col lg={12}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '300px 1fr',
                  gap: '20px',
                }}
                className="config-layout"
              >
                                {/* Left Sidebar */}
                <ConfigSidebar
                  companyName={tenantInfo.name}
                  companyLogo={tenantInfo.logo}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
                
                {/* Right Content */}
                <div>
                  {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </div>
                  )}
                  
                  <ConfigContentCard
                    title={tabInfo.title}
                    icon={tabInfo.icon}
                    loading={loading}
                    onReset={handleReset}
                    onSave={handleSave}
                  >
                    {activeTab === 'region' ? (
                      <RegionConfigForm
                        config={config}
                        loading={loading}
                        formikRef={regionFormRef}
                        ticketTypes={activeTicketTypes}
                        ticketTypesLoading={ticketTypesLoading}
                      />
                    ) : (
                      <LookupConfigForm
                        config={config}
                        loading={loading}
                        formikRef={lookupFormRef}
                      />
                    )}
                  </ConfigContentCard>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SystemConfiguration;